/**
 * @file `Emails` — namespacer for `admin.emails.*`, backing the `/emails`
 *       sub-app in the admin shell (#1598 Part 8).
 * @module @labcraft/admin/src/client/emails
 *
 * One `CoreLifecycle`-based namespacer per UI panel (per the workspace
 * Client-shape rule in `.agents/rules/services.md`). Owns no transport of
 * its own — the parent `Admin` adopts the shared `HttpTransport` and
 * hands a reference here at construction. Logs and metrics bubble under
 * `admin.emails.*`. Every method is a thin binding onto a route in
 * `../shared/emails_routes.ts`; the admin daemon's `Emails` service
 * validates + fulfils them.
 */

import { CoreLifecycle } from '@labcraft/core'
import type { HttpTransport } from './index.ts'
import {
  GetCampaignRoute,
  ListCampaignsRoute,
  ListEmailEventsRoute,
  ListSuppressionsRoute,
  ListSendersRoute,
  ResendCampaignRoute,
  ResolveRecipientsRoute,
  SendBroadcastRoute,
  TestSendRoute,
  type CampaignDetail,
  type EmailRecipientFilters,
  type ListCampaignsResponse,
  type ListEmailEventsResponse,
  type ListSuppressionsResponse,
  type ListSendersResponse,
  type ResolveRecipientsResponse,
  type SendBroadcastRequest,
  type SendBroadcastResponse,
  type TestSendRequest,
  type TestSendResponse,
} from '../shared/emails_routes.ts'

/**
 * Read + compose + send namespacer for the Emails sub-app. Held on the
 * frontend `Admin` Client; React components reach it via
 * `useClient<Admin>().emails`.
 */
export class Emails extends CoreLifecycle<{ id?: string }> {
  private readonly http: HttpTransport

  constructor(http: HttpTransport) {
    super({ id: 'emails' })
    this.http = http
  }

  /**
   * Resolve a recipient-filter set to its exact count (the compose
   * preview). Backs the "N recipients" readout the operator sees before
   * sending.
   *
   * @param filters - The recipient-subset filter set.
   * @returns `{ count }`.
   */
  async resolveRecipientCount(
    filters: EmailRecipientFilters,
  ): Promise<ResolveRecipientsResponse> {
    return this.http.request(ResolveRecipientsRoute, { body: filters })
  }

  /**
   * Compose + send a broadcast. Scope is either filter-based (via
   * `filters`) or explicit (via a non-empty `recipientIds`). Returns as soon
   * as the campaign is persisted + enqueued (#1627); delivery proceeds
   * asynchronously, so poll the campaign detail for live sent/delivered
   * counts rather than reading them off this response.
   *
   * @param args - Composed subject/body + recipient scope.
   * @returns The created campaign id + the enqueue tally.
   */
  async send(args: SendBroadcastRequest): Promise<SendBroadcastResponse> {
    // The send is now job-shaped (#1627): the daemon returns 202 as soon as
    // the campaign is enqueued, so the browser no longer holds a request open
    // across the fan-out and the default client timeout is ample. The #1628
    // stopgap timeout is removed.
    return this.http.request(SendBroadcastRoute, { body: args })
  }

  /**
   * Send a preview copy of the composed message to one address only — no
   * campaign, no suppression check, no tracking (#1606 Part 13).
   *
   * @param args - Composed `{ to, subject, body, format?, kind? }`.
   * @returns `{ status: 'sent', providerMessageId }`.
   */
  async testSend(args: TestSendRequest): Promise<TestSendResponse> {
    return this.http.request(TestSendRoute, { body: args })
  }

  /**
   * List campaigns newest-first with send aggregates.
   *
   * @returns `{ campaigns }`.
   */
  async listCampaigns(): Promise<ListCampaignsResponse> {
    return this.http.request(ListCampaignsRoute)
  }

  /**
   * List the unified email events (one-off + campaign) newest-first — the
   * History surface's read model (#1606 Part 10).
   *
   * @returns `{ events }`.
   */
  async listEvents(): Promise<ListEmailEventsResponse> {
    return this.http.request(ListEmailEventsRoute)
  }

  /**
   * Fetch one campaign with its per-recipient send + delivery detail.
   *
   * @param id - Campaign id.
   * @returns The campaign detail.
   */
  async getCampaign(id: string): Promise<CampaignDetail> {
    return this.http.request(GetCampaignRoute, { params: { id } })
  }

  /**
   * Re-send a campaign's body to only its bounced / failed recipients.
   * Requestless — the daemon reads the campaign + retry set itself.
   *
   * @param id - The source campaign id.
   * @returns The new campaign's tally (a zero tally when nothing needs
   *   re-sending).
   */
  async resendCampaign(id: string): Promise<SendBroadcastResponse> {
    return this.http.request(ResendCampaignRoute, { params: { id } })
  }

  /**
   * List suppressed addresses (newest opt-out first).
   *
   * @returns `{ suppressions }`.
   */
  async listSuppressions(): Promise<ListSuppressionsResponse> {
    return this.http.request(ListSuppressionsRoute)
  }

  /**
   * List the selectable sender identities (#1616) + the default Reply-To —
   * backs the compose From picker and the Reply-To default.
   *
   * @returns `{ senders, defaultReplyTo }`.
   */
  async listSenders(): Promise<ListSendersResponse> {
    return this.http.request(ListSendersRoute)
  }
}
